/**
 * 汎用ボタンコンポーネント
 * アプリ内で共通利用するボタンの表示を管理します
 */

import React from 'react';
import { Pressable, Text } from './WebPrimitives';

/**
 * 来場者テーマ用ボタンの見た目クラスを返す
 * @param {string} variant - ボタン種別
 * @param {boolean} disabled - 無効状態
 * @returns {string} Tailwindクラス文字列
 */
const buildVisitorButtonClassName = (variant, disabled) => {
  if (disabled) {
    return 'min-h-[48px] justify-center border-2 border-app-outline bg-app-surface-variant px-4 py-2';
  }

  if (variant === 'ghost') {
    return 'min-h-[48px] justify-center border-2 border-app-quest-night bg-[#EAF3FF] px-4 py-2';
  }

  if (variant === 'danger') {
    return 'min-h-[48px] justify-center border-2 border-[#7B1A1A] bg-app-danger px-4 py-2';
  }

  if (variant === 'secondary') {
    return 'min-h-[48px] justify-center border-2 border-app-quest-night bg-app-secondary px-4 py-2';
  }

  return 'min-h-[48px] justify-center border-2 border-[#0F1A2E] bg-app-quest-night px-4 py-2';
};

/**
 * 来場者テーマ用ボタンラベルの見た目クラスを返す
 * @param {string} variant - ボタン種別
 * @param {boolean} disabled - 無効状態
 * @returns {string} Tailwindクラス文字列
 */
const buildVisitorLabelClassName = (variant, disabled) => {
  if (disabled) {
    return 'text-center text-sm font-bold tracking-wide text-app-outline';
  }

  if (variant === 'ghost') {
    return 'text-center text-sm font-bold tracking-wide text-app-quest-night';
  }

  if (variant === 'danger') {
    return 'text-center text-sm font-bold tracking-wide text-app-primary-on';
  }

  if (variant === 'secondary') {
    return 'text-center text-sm font-bold tracking-wide text-app-quest-night';
  }

  return 'text-center text-sm font-bold tracking-wide text-app-primary-on';
};

/**
 * 管理テーマ用ボタンの見た目クラスを返す
 * @param {string} variant - ボタン種別
 * @param {boolean} disabled - 無効状態
 * @returns {string} Tailwindクラス文字列
 */
const buildAdminButtonClassName = (variant, disabled) => {
  if (disabled) {
    return 'min-h-[44px] justify-center rounded-lg border border-[#DADCE0] bg-[#F1F3F4] px-4 py-2';
  }

  if (variant === 'ghost') {
    return 'min-h-[44px] justify-center rounded-lg border border-[#DADCE0] bg-[#F8F9FA] px-4 py-2';
  }

  if (variant === 'danger') {
    return 'min-h-[44px] justify-center rounded-lg bg-[#EA4335] px-4 py-2';
  }

  if (variant === 'secondary') {
    return 'min-h-[44px] justify-center rounded-lg border border-[#DADCE0] bg-white px-4 py-2';
  }

  return 'min-h-[44px] justify-center rounded-lg bg-[#1A73E8] px-4 py-2';
};

/**
 * 管理テーマ用ボタンラベルの見た目クラスを返す
 * @param {string} variant - ボタン種別
 * @param {boolean} disabled - 無効状態
 * @returns {string} Tailwindクラス文字列
 */
const buildAdminLabelClassName = (variant, disabled) => {
  if (disabled) {
    return 'text-center text-sm font-semibold text-[#80868B]';
  }

  if (variant === 'ghost') {
    return 'text-center text-sm font-semibold text-[#1A73E8]';
  }

  if (variant === 'danger') {
    return 'text-center text-sm font-semibold text-white';
  }

  if (variant === 'secondary') {
    return 'text-center text-sm font-semibold text-[#1A73E8]';
  }

  return 'text-center text-sm font-semibold text-white';
};

/**
 * 汎用ボタン
 * @param {Object} props - プロパティ
 * @param {string} props.label - ボタンラベル
 * @param {() => void} props.onPress - 押下時処理
 * @param {boolean} [props.disabled=false] - 無効状態
 * @param {'primary' | 'secondary' | 'ghost' | 'danger'} [props.variant='primary'] - ボタン種別
 * @param {'visitor' | 'admin'} [props.theme='visitor'] - テーマ
 * @returns {JSX.Element} 汎用ボタン
 */
const AppButton = ({ label, onPress, disabled = false, variant = 'primary', theme = 'visitor' }) => {
  /** テーマに応じたクラス生成関数 */
  const isAdmin = theme === 'admin';

  /** ボタンコンテナクラス */
  const buttonClassName = isAdmin
    ? buildAdminButtonClassName(variant, disabled)
    : buildVisitorButtonClassName(variant, disabled);

  /** ラベルクラス */
  const labelClassName = isAdmin
    ? buildAdminLabelClassName(variant, disabled)
    : buildVisitorLabelClassName(variant, disabled);

  /** 影クラス */
  const shadowClassName = disabled
    ? ''
    : isAdmin
      ? 'shadow-sm'
      : 'shadow-[3px_3px_0_rgba(26,45,77,0.45)]';

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      className={`${buttonClassName} ${shadowClassName}`}
      disabled={disabled}
      onPress={onPress}
    >
      <Text className={labelClassName}>{label}</Text>
    </Pressable>
  );
};

export default AppButton;
